import { useState } from 'react'
import JSZip from 'jszip'

import { store } from 'app-redux/store'

import ToastService from 'services/toast.service'

import { DOUBLE_FACED_LAYOUTS } from 'types/card'

import PhatDropdown from 'components/formElements/PhatDropdown'
import LineLoader from 'components/elements/LineLoader'
import PhatButton from 'components/formElements/PhatButton'

import { getScryfallImageUrl, ImageSize } from 'utils/ImageUrl'

import styles from './downloadImagesModal.module.scss'

const DownloadImagesModal = () => {
  const [zippingImages, setZippingImages] = useState(false)
  const [imageType, setImageType] = useState<ImageSize>('large')

  const handleDownloadImages = async () => {
    const downloadingPng = imageType === 'png'
    const fileType = downloadingPng ? 'png' : 'jpg'
    const zip = new JSZip()
    const state = store.getState()

    const multiSelectedIds = Object.keys(state.active.multiSelectedIds)
    const cardMap = state.deck.cardMap

    const cards = multiSelectedIds.map(id => cardMap[id])

    setZippingImages(true)

    try {
      for (const card of cards) {
        const isFlipCard = DOUBLE_FACED_LAYOUTS.includes(card.layout)

        const imageUrl = getScryfallImageUrl(card.uid, false, imageType, undefined, downloadingPng)
        const imageBlob = await fetch(imageUrl).then(res => res.blob())
        const imageFile = new File([imageBlob], `filename.${fileType}`)

        zip.file(`${card.name.replace(/[^0-9a-z]/gi, '_')}.${fileType}`, imageFile, { base64: true })

        if (isFlipCard) {
          const imageUrlBack = getScryfallImageUrl(card.uid, true, imageType, undefined, downloadingPng)
          const imageBlobBack = await fetch(imageUrlBack).then(res => res.blob())
          const imageFileBack = new File([imageBlobBack], `filename.${fileType}`)

          zip.file(`${card.name.replace(/[^0-9a-z]/gi, '_')}_back.${fileType}`, imageFileBack, { base64: true })
        }
      }

      const result = await zip.generateAsync({ type: 'base64' })
      const element = document.createElement('a')
      const fileName = `card_images_${new Date().toLocaleDateString().replace(/\//g, '-')}.zip`

      element.setAttribute('href', 'data:application/zip;base64,' + result)
      element.setAttribute('download', fileName)
      element.style.display = 'none'

      document.body.appendChild(element)
      element.click()
      document.body.removeChild(element)
    } catch (err) {
      console.error(err)
      ToastService.create(
        'Failed to download one or more images. If this problem continues, please contact support.',
        'Download Helper',
        'error',
      )
    } finally {
      setZippingImages(false)
    }
  }

  return (
    <div>
      {zippingImages ? (
        <div className={styles.loadingStuff}>
          <h3>Zipping images</h3>
          <LineLoader />
          <p>
            This could take a few seconds, feel free to close this modal and the download window will appear when ready.
            (Or wait, I can't tell you what to do)
          </p>
        </div>
      ) : (
        <div>
          <PhatDropdown
            className={styles.dropdown}
            triggerWrapperClassName={styles.input}
            value={imageType}
            header="Image size"
            options={[
              { label: 'Small', onClick: () => setImageType('small'), id: 'small' },
              // { label: 'Normal', onClick: () => setImageType('normal'), id: 'normal' },
              { label: 'Large', onClick: () => setImageType('large'), id: 'large' },
              { label: 'Art Only', onClick: () => setImageType('art_crop'), id: 'art_crop' },
              { label: 'PNG', onClick: () => setImageType('png'), id: 'png' },
            ]}
          />

          {imageType === 'png' && (
            <div className={styles.pngWarning}>
              <p>
                <b>WARNING</b>
                <br />
                pngs take up orders of magnitude more space in RAM compared to other image types. Attempting to download
                too many at once will likely result in this browser tab crashing. If you want to download an entire
                deck, doing so in batches of less than 40 should be fine on most devices. The exact number can vary
                depending on the detail in the card image(s) and your device.
              </p>
            </div>
          )}

          <div className={styles.controls}>
            <PhatButton color="blue" onClick={handleDownloadImages}>
              Download
            </PhatButton>
          </div>
        </div>
      )}
    </div>
  )
}

export default DownloadImagesModal
